"use client";

import React from "react";
import { Check, PackageOpen, RotateCcw, Truck, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
const useWvcLanguage = () => {
  const [language, setLanguage] = React.useState<"en" | "zh" | "fr" | "es" | "de">("en");
  React.useEffect(() => {
    const handleStorage = () => {
      const stored = localStorage.getItem("wvc_language") || localStorage.getItem("wvc_lang") || "en";
      if (["en", "zh", "fr", "es", "de"].includes(stored)) {
        setLanguage(stored as "en" | "zh" | "fr" | "es" | "de");
      }
    };
    handleStorage();
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);
  return language;
};
const translations = {
  en: {
    heading: "Customer Care & Policies",
    shippingTitle: "Shipping &",
    returnsTitle: "returns",
    shippingDescription: "Every garment is packed with precision and sent with reliable tracking. Here is everything you need to know about receiving and exchanging your pieces.",
    complimentaryTitle: "Complimentary shipping",
    complimentaryDesc: "Carefully handled and tracked delivery for all orders.",
    complimentary1: "Complimentary standard delivery on all domestic orders over $100",
    complimentary2: "Standard domestic transit time of 3 to 5 business days",
    complimentary3: "Fully tracked international shipping via premium courier partners",
    complimentary4: "Express overnight dispatch options available at checkout",
    returnsCardTitle: "30-day returns",
    returnsCardDesc: "Effortless exchanges and straightforward refunds.",
    returns1: "Prepaid return shipping label included in every domestic parcel",
    returns2: "Items must be unwashed, unworn, and retain all original tags",
    returns3: "Refunds processed directly to original payment method within 5 days",
    returns4: "Complimentary size exchanges available with instant reservation",
    packagingTitle: "Sustainable packaging",
    packagingDesc: "Thoughtful materials designed to protect garments and minimize waste.",
    packaging1: "100% recyclable, FSC-certified cardboard presentation boxes",
    packaging2: "Plastic-free garment protective sleeves made from organic cellulose",
    packaging3: "Soy-based non-toxic printing inks and water-activated sealing tape",
    packaging4: "Designed for reusable return transit without requiring extra boxes",
    needAssistance: "Need assistance with an existing parcel?",
    conciergeText: "Our concierge team is available Monday through Friday, 9am to 6pm GMT to assist with tracking and custom delivery requests.",
    continueShopping: "Continue shopping",
    trackOrder: "Track order"
  },
  zh: {
    heading: "客户关怀与政策",
    shippingTitle: "运送与",
    returnsTitle: "退货",
    shippingDescription: "每件衣服都精心包装，并带有可靠的跟踪。这是您需要了解的关于接收和交换您的件的一切。",
    complimentaryTitle: "免费运送",
    complimentaryDesc: "所有订单的仔细处理和跟踪交付。",
    complimentary1: "所有超过$100的国内订单免费标准送货",
    complimentary2: "标准国内运输时间为3至5个工作日",
    complimentary3: "通过高级快递合作伙伴进行完全跟踪的国际运输",
    complimentary4: "在结账时可提供快速隔夜派遣选项",
    returnsCardTitle: "30天退货",
    returnsCardDesc: "轻松的交易和直接的退款。",
    returns1: "每个国内包裹都包括预付回程运单",
    returns2: "物品必须未洗过、未穿过，并保留所有原始标签",
    returns3: "退款在5天内直接处理到原始付款方式",
    returns4: "可提供免费尺码交换，可即时预订",
    packagingTitle: "可持续包装",
    packagingDesc: "精心设计的材料，可保护服装并最小化浪费。",
    packaging1: "100%可回收、FSC认证的纸板展示盒",
    packaging2: "由有机纤维素制成的无塑料服装保护套",
    packaging3: "大豆油性无毒印刷油墨和水激活封带",
    packaging4: "设计用于可重复使用的回程运输，无需额外的盒子",
    needAssistance: "需要帮助现有包裹吗？",
    conciergeText: "我们的礼宾团队周一至周五上午9点至下午6点GMT提供协助，以协助跟踪和自定义交付请求。",
    continueShopping: "继续购物",
    trackOrder: "跟踪订单"
  },
  fr: {
    heading: "Soins Client et Politiques",
    shippingTitle: "Livraison et",
    returnsTitle: "retours",
    shippingDescription: "Chaque vêtement est emballé avec précision et envoyé avec un suivi fiable. Voici tout ce que vous devez savoir sur la réception et l'échange de vos pièces.",
    complimentaryTitle: "Livraison gratuite",
    complimentaryDesc: "Livraison soignée et suivi pour toutes les commandes.",
    complimentary1: "Livraison standard gratuite sur toutes les commandes nationales de plus de 100$",
    complimentary2: "Délai de transit standard national de 3 à 5 jours ouvrables",
    complimentary3: "Livraison internationale entièrement suivie via des partenaires de courrier haut de gamme",
    complimentary4: "Options d'expédition express du jour au lendemain disponibles à la caisse",
    returnsCardTitle: "Retours de 30 jours",
    returnsCardDesc: "Échanges sans effort et remboursements directs.",
    returns1: "Étiquette d'expédition de retour prépayée incluse dans chaque colis national",
    returns2: "Les articles doivent être non lavés, non portés et conserver toutes les étiquettes d'origine",
    returns3: "Remboursements traités directement à la méthode de paiement d'origine dans les 5 jours",
    returns4: "Échanges de taille gratuits disponibles avec réservation instantanée",
    packagingTitle: "Emballage durable",
    packagingDesc: "Matériaux réfléchis conçus pour protéger les vêtements et minimiser les déchets.",
    packaging1: "Boîtes de présentation en carton 100% recyclable certifiées FSC",
    packaging2: "Manchons de protection des vêtements sans plastique fabriqués à partir de cellulose organique",
    packaging3: "Encres d'impression non toxiques à base de soja et ruban scellant activé à l'eau",
    packaging4: "Conçu pour le transit de retour réutilisable sans nécessiter de boîtes supplémentaires",
    needAssistance: "Besoin d'aide avec un colis existant?",
    conciergeText: "Notre équipe de concierge est disponible du lundi au vendredi, de 9h à 18h GMT pour vous aider avec le suivi et les demandes de livraison personnalisées.",
    continueShopping: "Continuer le shopping",
    trackOrder: "Suivre la commande"
  },
  es: {
    heading: "Cuidado del Cliente y Políticas",
    shippingTitle: "Envío y",
    returnsTitle: "devoluciones",
    shippingDescription: "Cada prenda se empaca con precisión y se envía con seguimiento confiable. Aquí está todo lo que necesitas saber sobre recibir e intercambiar tus piezas.",
    complimentaryTitle: "Envío gratuito",
    complimentaryDesc: "Entrega cuidadosa y rastreada para todos los pedidos.",
    complimentary1: "Envío estándar gratuito en todos los pedidos nacionales superiores a $100",
    complimentary2: "Tiempo de tránsito estándar nacional de 3 a 5 días hábiles",
    complimentary3: "Envío internacional totalmente rastreado a través de socios de mensajería premium",
    complimentary4: "Opciones de envío exprés del día a la noche disponibles en el pago",
    returnsCardTitle: "Devoluciones de 30 días",
    returnsCardDesc: "Cambios sin esfuerzo y reembolsos directos.",
    returns1: "Etiqueta de envío de devolución prepagada incluida en cada paquete nacional",
    returns2: "Los artículos deben estar sin lavar, sin usar y conservar todas las etiquetas originales",
    returns3: "Reembolsos procesados directamente al método de pago original dentro de 5 días",
    returns4: "Cambios de talla gratuitos disponibles con reserva instantánea",
    packagingTitle: "Embalaje sostenible",
    packagingDesc: "Materiales reflexivos diseñados para proteger prendas y minimizar desperdicios.",
    packaging1: "Cajas de presentación de cartón 100% reciclables certificadas por FSC",
    packaging2: "Fundas protectoras de prendas sin plástico hechas de celulosa orgánica",
    packaging3: "Tintas de impresión no tóxicas a base de soja y cinta de sellado activada por agua",
    packaging4: "Diseñado para transporte de retorno reutilizable sin requerir cajas adicionales",
    needAssistance: "¿Necesitas ayuda con un paquete existente?",
    conciergeText: "Nuestro equipo de conserjería está disponible de lunes a viernes, de 9 a.m. a 6 p.m. GMT para ayudar con el seguimiento y solicitudes de entrega personalizadas.",
    continueShopping: "Continuar comprando",
    trackOrder: "Rastrear orden"
  },
  de: {
    heading: "Kundenbetreuung und Richtlinien",
    shippingTitle: "Versand und",
    returnsTitle: "Rückgaben",
    shippingDescription: "Jedes Kleidungsstück wird sorgfältig verpackt und mit zuverlässiger Verfolgung versendet. Hier ist alles, was Sie über den Empfang und den Austausch Ihrer Stücke wissen müssen.",
    complimentaryTitle: "Kostenloser Versand",
    complimentaryDesc: "Sorgfältige und verfolgte Lieferung für alle Bestellungen.",
    complimentary1: "Kostenloser Standardversand bei allen inländischen Bestellungen über $100",
    complimentary2: "Standard-Inlandslaufzeit von 3 bis 5 Arbeitstagen",
    complimentary3: "Vollständig verfolgter internationaler Versand über Premium-Kurierdienste",
    complimentary4: "Express-Versandoptionen am nächsten Tag verfügbar",
    returnsCardTitle: "30-Tage-Rückgaben",
    returnsCardDesc: "Müheloser Austausch und unkomplizierte Rückerstattungen.",
    returns1: "Bezahltes Rücksendeetikett in jedem inländischen Paket enthalten",
    returns2: "Artikel müssen ungewaschen, ungetragen und mit allen Original-Tags versehen sein",
    returns3: "Rückerstattungen werden innerhalb von 5 Tagen direkt auf die ursprüngliche Zahlungsweise verarbeitet",
    returns4: "Kostenloser Größentausch verfügbar mit sofortiger Reservierung",
    packagingTitle: "Nachhaltige Verpackung",
    packagingDesc: "Bedacht gewählte Materialien zum Schutz von Kleidung und Minimierung von Abfällen.",
    packaging1: "100% recycelbare, FSC-zertifizierte Kartonpräsentationsboxen",
    packaging2: "Kunststofffreie Schutzhüllen aus organischer Zellulose",
    packaging3: "Ungiftige Druckfarben auf Sojabasis und wasseraktiviertes Verschlussband",
    packaging4: "Für wiederverwendbare Rückfahrt ohne zusätzliche Kartons konzipiert",
    needAssistance: "Benötigen Sie Hilfe mit einem vorhandenen Paket?",
    conciergeText: "Unser Concierge-Team ist von Montag bis Freitag, 9 bis 18 Uhr GMT, für Hilfe bei Verfolgung und benutzerdefinierten Lieferanfragen verfügbar.",
    continueShopping: "Weiter einkaufen",
    trackOrder: "Bestellung verfolgen"
  }
};
export default function Shipping() {
  const language = useWvcLanguage();
  const t = translations[language] || translations.en;
  const shippingPolicies = [{
    icon: Truck,
    title: t.complimentaryTitle,
    description: t.complimentaryDesc,
    points: [t.complimentary1, t.complimentary2, t.complimentary3, t.complimentary4]
  }, {
    icon: RotateCcw,
    title: t.returnsCardTitle,
    description: t.returnsCardDesc,
    points: [t.returns1, t.returns2, t.returns3, t.returns4]
  }, {
    icon: PackageOpen,
    title: t.packagingTitle,
    description: t.packagingDesc,
    points: [t.packaging1, t.packaging2, t.packaging3, t.packaging4]
  }];
  return <section id="配送与退换货" data-nav="light" className="bg-background text-foreground py-16 sm:py-20 lg:py-24 border-b border-border">
      <div className="mx-auto max-w-[1360px] px-6 sm:px-10 lg:px-16">
        {/* Header Section */}
        <div className="max-w-2xl mb-12 sm:mb-16">
          <p className="font-sans text-xs uppercase tracking-widest text-muted-foreground mb-3 font-semibold">
            {t.heading}
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight text-foreground leading-tight">
            {t.shippingTitle} <span className="italic">{t.returnsTitle}</span>
          </h2>
          <p className="font-sans text-muted-foreground text-base sm:text-lg mt-4 font-light leading-relaxed">
            {t.shippingDescription}
          </p>
        </div>

        {/* 3-Column Policy Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {shippingPolicies.map((policy, i) => {
          const IconComponent = policy.icon;
          return <div key={policy.title} data-index={i} className="flex">
                <Card className="w-full bg-card text-card-foreground border border-border rounded-none shadow-none flex flex-col justify-between hover:border-foreground/30 transition-colors duration-200">
                  <div>
                    <CardHeader className="p-8 pb-4">
                      <div className="w-12 h-12 flex items-center justify-center border border-border bg-background mb-6">
                        <IconComponent className="size-5 text-foreground stroke-[1.25]" />
                      </div>
                      <CardTitle className="font-serif text-2xl font-normal text-foreground">
                        {policy.title}
                      </CardTitle>
                      <CardDescription className="font-sans text-muted-foreground text-sm mt-2">
                        {policy.description}
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="px-8 pb-8 pt-2">
                      <div className="w-full h-px bg-border my-4" />
                      <ul className="space-y-3.5">
                        {policy.points.map((point, pointIndex) => <li key={pointIndex} data-index={pointIndex} className="flex items-start gap-3 font-sans text-sm text-foreground/90 leading-normal">
                            <span className="mt-1 flex items-center justify-center size-4 shrink-0 border border-border text-foreground">
                              <Check className="size-2.5 stroke-[2]" />
                            </span>
                            <span>{point}</span>
                          </li>)}
                      </ul>
                    </CardContent>
                  </div>
                </Card>
              </div>;
        })}
        </div>

        {/* Assistance / Support Footer Banner */}
        <div className="mt-12 lg:mt-16 p-8 border border-border bg-card flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div className="max-w-xl">
            <h3 className="font-serif text-xl sm:text-2xl font-normal text-foreground">
              {t.needAssistance}
            </h3>
            <p className="font-sans text-sm text-muted-foreground mt-1">
              {t.conciergeText}
            </p>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            <Button asChild variant="default" className="rounded-none px-6">
              <Link to="/ShopPage">
                {t.continueShopping}
              </Link>
            </Button>
            <Button asChild variant="outline" className="rounded-none px-6">
              <Link to="/CheckoutPage" className="inline-flex items-center gap-2">
                <span>{t.trackOrder}</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}